import React, { useState } from 'react';
import { Modal, Form, Input, Select, Checkbox, Button, Tabs, Card, Space } from 'antd';
import { ArrowLeftOutlined, ExclamationCircleOutlined, WarningOutlined } from '@ant-design/icons';
import {
  DeviceType,
  RulesWithDeviceType,
  SuperGroup,
  DeviceTypeSettings,
  Version,
  ApiError,
  StbGroupExtended,
} from '@services/types';

import styles from './rules.module.scss';
import api from '@services/api';
import { toast } from 'react-toastify';
import { selectSearchFilter } from '@services/utils';
import { AppContext } from '@layouts/AppRouter';
import { DisableExtendedVersioningSettingsRequest, UpgradeRuleValidateInfoResponse } from '@openapi';
import { useHistory, useParams } from 'react-router-dom';
import { Navigation } from '@constants/Navigation';

const { Option } = Select;
const { TabPane } = Tabs;

const EditRuleModal = () => {
  const [form] = Form.useForm<RulesWithDeviceType>();
  const [ruleToUpdate, setRuleToUpdate] = useState<RulesWithDeviceType>();
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [superGroups, setSuperGroups] = useState<SuperGroup[]>([]);
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { settings } = React.useContext(AppContext);
  const [deviceSettings, setDeviceSettings] = React.useState<DeviceTypeSettings>();
  const [deviceVersion, setDeviceVersion] = React.useState<Version>();
  const [deviceTypeId, setDeviceTypeId] = React.useState<DeviceType['id']>('');
  const [checkedCompareFw, setCheckedCompareFw] = useState<RulesWithDeviceType['enableFwCondition']>();
  const [checkedCompareUi, setCheckedCompareUi] = useState<RulesWithDeviceType['enableUiCondition']>();
  const [checkedCompareLoad, setCheckedCompareLoad] = useState<RulesWithDeviceType['enableLoaderCondition']>();
  const [checkedExtendedLoad, setCheckedExtendedLoad] =
    useState<DisableExtendedVersioningSettingsRequest['disableExtendedLoaderVersioning']>();
  const [checkedExtendedFw, setCheckedExtendedFw] =
    useState<DisableExtendedVersioningSettingsRequest['disableExtendedFwVersioning']>();
  const [checkedExtendedUi, setCheckedExtendedUi] =
    useState<DisableExtendedVersioningSettingsRequest['disableExtendedUiVersioning']>();
  const [checkedExtendedCFW1, setCheckedExtendedCFW1] =
    useState<DisableExtendedVersioningSettingsRequest['disableExtendedCustomFw1Versioning']>();
  const [checkedExtendedCFW2, setCheckedExtendedCFW2] =
    useState<DisableExtendedVersioningSettingsRequest['disableExtendedCustomFw2Versioning']>();
  const [checkedExtendedCFW3, setCheckedExtendedCFW3] =
    useState<DisableExtendedVersioningSettingsRequest['disableExtendedCustomFw3Versioning']>();
  const [visibleWarningModal, setVisibleWarningModal] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('1');
  const [accessTab, setAccessTab] = useState<string>('1');
  const [warningData, setWarningData] = useState<UpgradeRuleValidateInfoResponse>();

  const handleNext = () => {
    if (activeTab === '1') {
      setActiveTab('2');
      setAccessTab('2');
    } else if (activeTab === '2') {
      setActiveTab('3');
      setAccessTab('3');
    }
  };
  const onClose = () => {
    history.push(Navigation.Rules);
  };
  const displayWarning = () => {
    if (id) {
      api.rules.getWarningRule(id).then((data) => {
        setWarningData(data);
        setVisibleWarningModal(true);
      });
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (ruleToUpdate) {
        await api.rules.updateRule({ ...values, id: id });
        toast.success('Rule was updated successfully');
      } else {
        await api.rules.createUpgradeRule(values);
        toast.success('Rule was created successfully');
      }
      form.resetFields();
      onClose();
    } catch (response) {
      if (response instanceof ApiError) {
        toast.error(`${response.error.details || Object.values(response.error.propertyErrors!).map((item) => item)}`);
      } else {
        toast.error('Something went wrong');
      }
      console.log('CreateRule ~ handleSubmit ~ error', response);
    }
  };

  React.useEffect(() => {
    if (ruleToUpdate?.deviceTypeId || deviceTypeId) {
      Promise.all([
        api.administration.getDeviceTypeSettings(ruleToUpdate?.deviceTypeId || deviceTypeId),
        api.administration.getDeviceTypeVersions(ruleToUpdate?.deviceTypeId || deviceTypeId),
      ])
        .then(([data, version]) => {
          setDeviceSettings(data);
          setDeviceVersion(version);
        })
        .catch((e) => {
          console.log(e);
        });
    }
  }, [deviceTypeId, ruleToUpdate?.deviceTypeId]);

  React.useEffect(() => {
    Promise.all([api.administration.getDeviceTypes(), api.administration.getSuperGroups()])
      .then(([deviceType, superGroup]) => {
        if (deviceType && superGroup) {
          setDeviceTypes(deviceType);
          setSuperGroups(superGroup);
        }
      })
      .catch((error) => {
        console.error(error);
        toast.error('Firmwares was not loaded due to error');
      });
  }, []);

  React.useEffect(() => {
    if (id) {
      api.rules.getRule(id).then((rule) => {
        if (rule) {
          setRuleToUpdate({
            ...rule,
            deviceTypeName: deviceTypes.find((deviceType) => deviceType.id === rule.deviceTypeId)?.deviceName,
            superGroupName: superGroups.find((superGroup) => superGroup.id === rule.deviceTypeId)?.name,
          });
          setCheckedCompareFw(ruleToUpdate?.enableFwCondition);
          setCheckedCompareLoad(ruleToUpdate?.enableLoaderCondition);
          setCheckedCompareUi(ruleToUpdate?.enableUiCondition);
          setCheckedExtendedFw(ruleToUpdate?.disableExtendedVersioningSettings?.disableExtendedFwVersioning);
          setCheckedExtendedLoad(ruleToUpdate?.disableExtendedVersioningSettings?.disableExtendedLoaderVersioning);
          setCheckedExtendedUi(ruleToUpdate?.disableExtendedVersioningSettings?.disableExtendedUiVersioning);
          setCheckedExtendedCFW1(ruleToUpdate?.disableExtendedVersioningSettings?.disableExtendedCustomFw1Versioning);
          setCheckedExtendedCFW2(ruleToUpdate?.disableExtendedVersioningSettings?.disableExtendedCustomFw2Versioning);
          setCheckedExtendedCFW3(ruleToUpdate?.disableExtendedVersioningSettings?.disableExtendedCustomFw3Versioning);
        }
      });
    }
  }, [deviceTypes, id, superGroups]);

  return ruleToUpdate ? (
    <Card
      title="Modify Upgrade rule"
      extra={
        <Space>
          <Button icon={<ArrowLeftOutlined />} type="default" onClick={onClose}>
            Back to Rule Page
          </Button>
          <Button type="primary" onClick={id ? displayWarning : handleSubmit}>
            Update
          </Button>
        </Space>
      }
    >
      <div>
        <Form form={form} layout="vertical" name="form_in_modal" className={styles.form} initialValues={ruleToUpdate}>
          <Form.Item
            className={styles.block}
            name="deviceTypeId"
            style={{ gridArea: 'deviceTypeId' }}
            label="Device Type"
            rules={[
              {
                required: true,
                message: 'Please input the Device Type',
              },
            ]}
          >
            <Select
              onChange={(e: DeviceType['id']) => {
                setDeviceTypeId(e);
              }}
              disabled={!!ruleToUpdate}
              allowClear
              showSearch
              defaultValue={deviceTypeId}
              filterOption={selectSearchFilter}
            >
              {deviceTypes?.map((item) => {
                return (
                  <Option key={item.id} value={item.id}>
                    {item.deviceName}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item
            className={styles.block}
            name="superGroupId"
            style={{ gridArea: 'superGroup' }}
            label="Scope"
            rules={[
              {
                required: true,
                message: 'Please input the Scope',
              },
            ]}
          >
            <Select disabled={!!ruleToUpdate} allowClear showSearch filterOption={selectSearchFilter}>
              {superGroups?.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            className={styles.block}
            style={{ gridArea: 'name' }}
            name="name"
            label="Name"
            rules={[
              {
                required: true,
                message: 'Name can not be empty!',
              },
            ]}
          >
            <Input allowClear />
          </Form.Item>
          {!!ruleToUpdate && (
            <Form.Item
              className={styles.block}
              style={{ gridArea: 'stbGroups' }}
              name="Affected StbGroups"
              label="Affected StbGroups:"
            >
              <div
                style={{
                  fontWeight: '400',
                  fontSize: '13px',
                  backgroundColor: '#1890ff1c',
                  height: 'fit-content',
                  overflow: 'auto',
                }}
              >
                {ruleToUpdate?.stbGroups?.map((item) => item).join(', ') || 'There are no affected groups'}
              </div>
            </Form.Item>
          )}
          {settings?.enableDisablingExtendedVersioning && (
            <>
              <Form.Item
                name={['disableExtendedVersioningSettings', 'disableExtendedLoaderVersioning']}
                valuePropName="checked"
                className={styles.block}
                style={{ gridArea: 'extendedLoader' }}
              >
                <Checkbox
                  onChange={() => {
                    setCheckedExtendedLoad(!checkedExtendedLoad);
                  }}
                >
                  Disable Extended Loader Version
                </Checkbox>
              </Form.Item>
              <Form.Item
                name={['disableExtendedVersioningSettings', 'disableExtendedFwVersioning']}
                valuePropName="checked"
                className={styles.block}
                style={{ gridArea: 'extendedFw' }}
              >
                <Checkbox
                  onChange={() => {
                    setCheckedExtendedFw(!checkedExtendedFw);
                  }}
                >
                  Disable Extended FW Version
                </Checkbox>
              </Form.Item>
              <Form.Item
                name={['disableExtendedVersioningSettings', 'disableExtendedUiVersioning']}
                valuePropName="checked"
                className={styles.block}
                style={{ gridArea: 'extendedUi' }}
              >
                <Checkbox
                  onChange={() => {
                    setCheckedExtendedUi(!checkedExtendedUi);
                  }}
                >
                  Disable Extended UI Version
                </Checkbox>
              </Form.Item>
              {deviceSettings?.fwExtraName1 && (
                <Form.Item
                  name={['disableExtendedVersioningSettings', 'disableExtendedCustomFw1Versioning']}
                  valuePropName="checked"
                  className={styles.block}
                  style={{ gridArea: 'extendedCustomOne' }}
                >
                  <Checkbox
                    onChange={() => {
                      setCheckedExtendedCFW1(!checkedExtendedCFW1);
                    }}
                  >
                    Disable Extended CFW1 Version
                  </Checkbox>
                </Form.Item>
              )}
              {deviceSettings?.fwExtraName2?.length && deviceSettings.fwExtraName2 && (
                <Form.Item
                  name={['disableExtendedVersioningSettings', 'disableExtendedCustomFw2Versioning']}
                  valuePropName="checked"
                  className={styles.block}
                  style={{ gridArea: 'extendedCustomTwo' }}
                >
                  <Checkbox
                    onChange={() => {
                      setCheckedExtendedCFW2(!checkedExtendedCFW2);
                    }}
                  >
                    Disable Extended CFW2 Version
                  </Checkbox>
                </Form.Item>
              )}
              {deviceSettings?.fwExtraName3 && deviceSettings?.fwExtraName3?.length && deviceSettings.fwExtraName3 && (
                <Form.Item
                  name={['disableExtendedVersioningSettings', 'disableExtendedCustomFw3Versioning']}
                  valuePropName="checked"
                  className={styles.block}
                  style={{ gridArea: 'extendedCustomThree' }}
                >
                  <Checkbox
                    onChange={() => {
                      setCheckedExtendedCFW3(!checkedExtendedCFW3);
                    }}
                  >
                    Disable Extended CFW3 Version
                  </Checkbox>
                </Form.Item>
              )}
            </>
          )}
          {deviceVersion?.LOADER && (
            <Form.Item
              className={styles.block}
              name="loaderVersionFirmwareId"
              style={{ gridArea: 'loaderVersion' }}
              label="Loader Version"
              rules={[
                {
                  required: false,
                  message: 'Please choose the Loader Version!',
                },
              ]}
            >
              <Select allowClear showSearch filterOption={selectSearchFilter}>
                {(deviceTypeId || ruleToUpdate?.deviceTypeId) &&
                  deviceVersion?.LOADER?.map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {item.version}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          )}
          {deviceVersion?.FW && (
            <Form.Item
              className={styles.block}
              name="fwVersionFirmwareId"
              style={{ gridArea: 'FWVersion' }}
              label="FW Version"
              rules={[
                {
                  required: false,
                  message: 'Please choose the FW Version!',
                },
              ]}
            >
              <Select allowClear showSearch filterOption={selectSearchFilter}>
                {(deviceTypeId || ruleToUpdate?.deviceTypeId) &&
                  deviceVersion?.FW?.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.version}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          )}
          {deviceVersion?.UI && (
            <Form.Item
              className={styles.block}
              name="uiVersionFirmwareId"
              style={{ gridArea: 'UIVersion' }}
              label="UI Version"
              rules={[
                {
                  required: false,
                  message: 'Please choose the UI Version!',
                },
              ]}
            >
              <Select allowClear showSearch filterOption={selectSearchFilter}>
                {(deviceTypeId || ruleToUpdate?.deviceTypeId) &&
                  deviceVersion?.UI?.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.version}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          )}
          {deviceSettings?.fwExtraName1 && (
            <Form.Item
              className={styles.block}
              name="customFirmware1VersionId"
              style={{ gridArea: 'customOne' }}
              label={deviceSettings?.fwExtraName1}
              rules={[
                {
                  required: false,
                  message: 'Please choose the Custom One Version',
                },
              ]}
            >
              <Select allowClear showSearch filterOption={selectSearchFilter}>
                {(deviceTypeId || ruleToUpdate?.deviceTypeId) &&
                  deviceVersion?.CUSTOFW1?.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.version}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          )}
          {deviceSettings?.fwExtraName2 && (
            <Form.Item
              className={styles.block}
              name="customFirmware2VersionId"
              style={{ gridArea: 'customTwo' }}
              label={deviceSettings?.fwExtraName2}
              rules={[
                {
                  required: false,
                  message: 'Please choose the Custom One Version',
                },
              ]}
            >
              <Select allowClear showSearch filterOption={selectSearchFilter}>
                {(deviceTypeId || ruleToUpdate?.deviceTypeId) &&
                  deviceVersion?.CUSTOFW2?.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.version}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          )}
          {deviceSettings?.fwExtraName3 && (
            <Form.Item
              className={styles.block}
              name="customFirmware3VersionId"
              style={{ gridArea: 'customThree' }}
              label={deviceSettings?.fwExtraName3}
              rules={[
                {
                  required: false,
                  message: 'Please choose the Custom One Version',
                },
              ]}
            >
              <Select allowClear showSearch filterOption={selectSearchFilter}>
                {(deviceTypeId || ruleToUpdate?.deviceTypeId) &&
                  deviceVersion?.CUSTOFW3?.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.version}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          )}
          {settings?.enableCompareFwVersions && (
            <>
              <Form.Item
                name="enableFwCondition"
                valuePropName="checked"
                className={styles.block}
                style={{ gridArea: 'enableCompareFwVersions' }}
              >
                <Checkbox
                  onChange={() => {
                    if (checkedCompareFw != undefined) {
                      setCheckedCompareFw(!checkedCompareFw);
                    }
                  }}
                >
                  Enable Compare FW Versions
                </Checkbox>
              </Form.Item>

              <Form.Item
                name="minFwVersion"
                label="Min FW Version"
                className={styles.block}
                style={{ gridArea: 'fwVersionOne' }}
              >
                <Select
                  disabled={checkedCompareFw != undefined && !checkedCompareFw}
                  allowClear
                  showSearch
                  filterOption={selectSearchFilter}
                >
                  {(deviceTypeId || ruleToUpdate?.deviceTypeId) &&
                    deviceVersion?.FW?.map((item) => (
                      <Option key={item.id} value={item.version}>
                        {item.version}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="maxFwVersion"
                label="Max FW Version"
                className={styles.block}
                style={{ gridArea: 'fwVersionTwo' }}
              >
                <Select
                  disabled={checkedCompareFw != undefined && !checkedCompareFw}
                  allowClear
                  showSearch
                  filterOption={selectSearchFilter}
                >
                  {(deviceTypeId || ruleToUpdate?.deviceTypeId) &&
                    deviceVersion?.FW?.map((item) => (
                      <Option key={item.id} value={item.version}>
                        {item.version}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="enableUiCondition"
                valuePropName="checked"
                className={styles.block}
                style={{ gridArea: 'enableCompareUiVersions' }}
              >
                <Checkbox
                  onChange={() => {
                    if (checkedCompareUi != undefined) {
                      setCheckedCompareUi(!checkedCompareUi);
                    }
                  }}
                >
                  Enable Compare UI Versions
                </Checkbox>
              </Form.Item>
              <Form.Item
                name="minUiVersion"
                label="Min UI Version"
                className={styles.block}
                style={{ gridArea: 'uiVersionOne' }}
              >
                <Select
                  disabled={!checkedCompareUi != undefined && !checkedCompareUi}
                  allowClear
                  showSearch
                  filterOption={selectSearchFilter}
                >
                  {(deviceTypeId || ruleToUpdate?.deviceTypeId) &&
                    deviceVersion?.UI?.map((item) => (
                      <Option key={item.id} value={item.version}>
                        {item.version}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="maxUiVersion"
                label="Max UI Version"
                className={styles.block}
                style={{ gridArea: 'uiVersionTwo' }}
              >
                <Select
                  disabled={!checkedCompareUi != undefined && !checkedCompareUi}
                  allowClear
                  showSearch
                  filterOption={selectSearchFilter}
                >
                  {(deviceTypeId || ruleToUpdate?.deviceTypeId) &&
                    deviceVersion?.UI?.map((item) => (
                      <Option key={item.id} value={item.version}>
                        {item.version}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="enableLoaderCondition"
                valuePropName="checked"
                className={styles.block}
                style={{ gridArea: 'enableCompareLoaderVersions' }}
              >
                <Checkbox
                  onChange={() => {
                    if (checkedCompareLoad != undefined) {
                      setCheckedCompareLoad(!checkedCompareLoad);
                    }
                  }}
                >
                  Enable Compare Loader Versions
                </Checkbox>
              </Form.Item>
              <Form.Item
                name="minLoaderVersion"
                label="Min Loader Version"
                className={styles.block}
                style={{ gridArea: 'loaderVersionOne' }}
              >
                <Select
                  disabled={!checkedCompareLoad != undefined && !checkedCompareLoad}
                  allowClear
                  showSearch
                  filterOption={selectSearchFilter}
                >
                  {(deviceTypeId || ruleToUpdate?.deviceTypeId) &&
                    deviceVersion?.LOADER?.map((item) => (
                      <Select.Option key={item.id} value={item.version}>
                        {item.version}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="maxLoaderVersion"
                label="Max Loader Version"
                className={styles.block}
                style={{ gridArea: 'loaderVersionTwo' }}
              >
                <Select
                  disabled={!checkedCompareLoad != undefined && !checkedCompareLoad}
                  allowClear
                  showSearch
                  filterOption={selectSearchFilter}
                >
                  {(deviceTypeId || ruleToUpdate?.deviceTypeId) &&
                    deviceVersion?.LOADER?.map((item) => (
                      <Select.Option key={item.id} value={item.version}>
                        {item.version}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </>
          )}
          {deviceSettings?.allowDowngradeUr && (
            <Form.Item
              name="forceUpdate"
              valuePropName="checked"
              className={styles.block}
              style={{ gridArea: 'forcedRule' }}
            >
              <Checkbox>Forced rule</Checkbox>
            </Form.Item>
          )}
          {deviceSettings?.urSendAllAtOnce && (
            <Form.Item
              name="sendAllFirmwares"
              valuePropName="checked"
              className={styles.block}
              style={{ gridArea: 'sendFirmOnce' }}
            >
              <Checkbox>Send all firmwares at once</Checkbox>
            </Form.Item>
          )}
        </Form>
      </div>
      <Modal
        style={{ top: '25%' }}
        title={
          <>
            <WarningOutlined style={{ color: '#faad14', marginRight: '8px' }} />
            Warning Update Modal
          </>
        }
        onCancel={onClose}
        visible={visibleWarningModal}
        footer={
          <Button onClick={activeTab === '3' ? handleSubmit : handleNext}>
            {activeTab === '3' ? 'Update' : 'Next'}
          </Button>
        }
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane
            style={{ maxHeight: '200px', overflowY: 'auto' }}
            tab="Affected Groups"
            key="1"
            forceRender
            disabled={parseInt(accessTab) < 1}
          >
            {warningData?.groups?.map((item, index) => (
              <div key={index}>
                <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: '8px' }} />
                <span>{item}</span>
              </div>
            ))}
          </TabPane>
          <TabPane tab="Devices" key="2" forceRender disabled={parseInt(accessTab) < 2}>
            <div>
              <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: '8px' }} />
              <span>{`Devices number: ${warningData?.deviceNumber}`}</span>
            </div>
          </TabPane>
          <TabPane tab="Attached to default group" key="3" forceRender disabled={parseInt(accessTab) < 3}>
            <div>
              <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: '8px' }} />
              {warningData?.attachedToDefaultGroup ? 'Attached to default group' : 'Not attached to default group'}
            </div>
          </TabPane>
        </Tabs>
      </Modal>
    </Card>
  ) : !id ? (
    <Card
      title="Add New Upgrade rule"
      extra={
        <Space>
          <Button icon={<ArrowLeftOutlined />} type="default" onClick={onClose}>
            Back to Rule Page
          </Button>
          <Button type="primary" onClick={handleSubmit}>
            Create
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        name="form_in_modal"
        className={styles.form}
        initialValues={{
          deviceTypeId: null,
          name: null,
          superGroupId: null,
          forceUpdate: null,
          loaderVersionFirmwareId: null,
          fwVersionFirmwareId: null,
          uiVersionFirmwareId: null,
          sendAllFirmwares: null,
          customFirmware1VersionId: null,
          customFirmware2VersionId: null,
          customFirmware3VersionId: null,
          enableLoaderCondition: false,
          enableUiCondition: false,
          enableFwCondition: false,
          disableExtendedVersioningSettings: {
            disableExtendedLoaderVersioning: false,
            disableExtendedFwVersioning: false,
            disableExtendedUiVersioning: false,
            disableExtendedCustomFw1Versioning: false,
            disableExtendedCustomFw2Versioning: false,
            disableExtendedCustomFw3Versioning: false,
          },
        }}
      >
        <Form.Item
          className={styles.block}
          name="deviceTypeId"
          style={{ gridArea: 'deviceTypeId' }}
          label="Device Type"
          rules={[
            {
              required: true,
              message: 'Please input the Device Type',
            },
          ]}
        >
          <Select
            onChange={(e: DeviceType['id']) => {
              setDeviceTypeId(e);
            }}
            disabled={!!ruleToUpdate}
            allowClear
            showSearch
            defaultValue={deviceTypeId}
            filterOption={selectSearchFilter}
          >
            {deviceTypes?.map((item) => {
              return (
                <Option key={item.id} value={item.id}>
                  {item.deviceName}
                </Option>
              );
            })}
          </Select>
        </Form.Item>
        <Form.Item
          className={styles.block}
          name="superGroupId"
          style={{ gridArea: 'superGroup' }}
          label="Scope"
          rules={[
            {
              required: true,
              message: 'Please input the Scope',
            },
          ]}
        >
          <Select disabled={!!ruleToUpdate} allowClear showSearch filterOption={selectSearchFilter}>
            {superGroups?.map((item) => (
              <Option key={item.id} value={item.id}>
                {item.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          className={styles.block}
          style={{ gridArea: 'name' }}
          name="name"
          label="Name"
          rules={[
            {
              required: true,
              message: 'Name can not be empty!',
            },
          ]}
        >
          <Input allowClear />
        </Form.Item>
        {settings?.enableDisablingExtendedVersioning && (
          <>
            <Form.Item
              name={['disableExtendedVersioningSettings', 'disableExtendedLoaderVersioning']}
              valuePropName="checked"
              className={styles.block}
              style={{ gridArea: 'extendedLoader' }}
            >
              <Checkbox
                onChange={() => {
                  setCheckedExtendedLoad(!checkedExtendedLoad);
                }}
              >
                Disable Extended Loader Version
              </Checkbox>
            </Form.Item>
            <Form.Item
              name={['disableExtendedVersioningSettings', 'disableExtendedFwVersioning']}
              valuePropName="checked"
              className={styles.block}
              style={{ gridArea: 'extendedFw' }}
            >
              <Checkbox
                onChange={() => {
                  setCheckedExtendedFw(!checkedExtendedFw);
                }}
              >
                Disable Extended FW Version
              </Checkbox>
            </Form.Item>
            <Form.Item
              name={['disableExtendedVersioningSettings', 'disableExtendedUiVersioning']}
              valuePropName="checked"
              className={styles.block}
              style={{ gridArea: 'extendedUi' }}
            >
              <Checkbox
                onChange={() => {
                  setCheckedExtendedUi(!checkedExtendedUi);
                }}
              >
                Disable Extended UI Version
              </Checkbox>
            </Form.Item>
            {deviceSettings?.fwExtraName1 && (
              <Form.Item
                name={['disableExtendedVersioningSettings', 'disableExtendedCustomFw1Versioning']}
                valuePropName="checked"
                className={styles.block}
                style={{ gridArea: 'extendedCustomOne' }}
              >
                <Checkbox
                  onChange={() => {
                    setCheckedExtendedCFW1(!checkedExtendedCFW1);
                  }}
                >
                  Disable Extended CFW1 Version
                </Checkbox>
              </Form.Item>
            )}
            {deviceSettings?.fwExtraName2?.length && deviceSettings.fwExtraName2 && (
              <Form.Item
                name={['disableExtendedVersioningSettings', 'disableExtendedCustomFw2Versioning']}
                valuePropName="checked"
                className={styles.block}
                style={{ gridArea: 'extendedCustomTwo' }}
              >
                <Checkbox
                  onChange={() => {
                    setCheckedExtendedCFW2(!checkedExtendedCFW2);
                  }}
                >
                  Disable Extended CFW2 Version
                </Checkbox>
              </Form.Item>
            )}
            {deviceSettings?.fwExtraName3 && deviceSettings?.fwExtraName3?.length && deviceSettings.fwExtraName3 && (
              <Form.Item
                name={['disableExtendedVersioningSettings', 'disableExtendedCustomFw3Versioning']}
                valuePropName="checked"
                className={styles.block}
                style={{ gridArea: 'extendedCustomThree' }}
              >
                <Checkbox
                  onChange={() => {
                    setCheckedExtendedCFW3(!checkedExtendedCFW3);
                  }}
                >
                  Disable Extended CFW3 Version
                </Checkbox>
              </Form.Item>
            )}
          </>
        )}
        {deviceVersion?.LOADER && (
          <Form.Item
            className={styles.block}
            name="loaderVersionFirmwareId"
            style={{ gridArea: 'loaderVersion' }}
            label="Loader Version"
            rules={[
              {
                required: false,
                message: 'Please choose the Loader Version!',
              },
            ]}
          >
            <Select allowClear showSearch filterOption={selectSearchFilter}>
              {deviceTypeId &&
                deviceVersion?.LOADER?.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.version}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        )}
        {deviceVersion?.FW && (
          <Form.Item
            className={styles.block}
            name="fwVersionFirmwareId"
            style={{ gridArea: 'FWVersion' }}
            label="FW Version"
            rules={[
              {
                required: false,
                message: 'Please choose the FW Version!',
              },
            ]}
          >
            <Select allowClear showSearch filterOption={selectSearchFilter}>
              {deviceTypeId &&
                deviceVersion?.FW?.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {item.version}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        )}
        {deviceVersion?.UI && (
          <Form.Item
            className={styles.block}
            name="uiVersionFirmwareId"
            style={{ gridArea: 'UIVersion' }}
            label="UI Version"
            rules={[
              {
                required: false,
                message: 'Please choose the UI Version!',
              },
            ]}
          >
            <Select allowClear showSearch filterOption={selectSearchFilter}>
              {deviceTypeId &&
                deviceVersion?.UI?.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {item.version}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        )}
        {deviceSettings?.fwExtraName1 && (
          <Form.Item
            className={styles.block}
            name="customFirmware1VersionId"
            style={{ gridArea: 'customOne' }}
            label={deviceSettings?.fwExtraName1}
            rules={[
              {
                required: false,
                message: 'Please choose the Custom One Version',
              },
            ]}
          >
            <Select allowClear showSearch filterOption={selectSearchFilter}>
              {deviceTypeId &&
                deviceVersion?.CUSTOFW1?.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {item.version}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        )}
        {deviceSettings?.fwExtraName2 && (
          <Form.Item
            className={styles.block}
            name="customFirmware2VersionId"
            style={{ gridArea: 'customTwo' }}
            label={deviceSettings?.fwExtraName2}
            rules={[
              {
                required: false,
                message: 'Please choose the Custom One Version',
              },
            ]}
          >
            <Select allowClear showSearch filterOption={selectSearchFilter}>
              {deviceTypeId &&
                deviceVersion?.CUSTOFW2?.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {item.version}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        )}
        {deviceSettings?.fwExtraName3 && (
          <Form.Item
            className={styles.block}
            name="customFirmware3VersionId"
            style={{ gridArea: 'customThree' }}
            label={deviceSettings?.fwExtraName3}
            rules={[
              {
                required: false,
                message: 'Please choose the Custom One Version',
              },
            ]}
          >
            <Select allowClear showSearch filterOption={selectSearchFilter}>
              {deviceTypeId &&
                deviceVersion?.CUSTOFW3?.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {item.version}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        )}
        {settings?.enableCompareFwVersions && (
          <>
            <Form.Item
              name="enableFwCondition"
              valuePropName="checked"
              className={styles.block}
              style={{ gridArea: 'enableCompareFwVersions' }}
            >
              <Checkbox
                onChange={() => {
                  setCheckedCompareFw(!checkedCompareFw);
                }}
              >
                Enable Compare FW Versions
              </Checkbox>
            </Form.Item>
            <Form.Item
              name="enableUiCondition"
              valuePropName="checked"
              className={styles.block}
              style={{ gridArea: 'enableCompareUiVersions' }}
            >
              <Checkbox
                onChange={() => {
                  setCheckedCompareUi(!checkedCompareUi);
                }}
              >
                Enable Compare UI Versions
              </Checkbox>
            </Form.Item>
            <Form.Item
              name="enableLoaderCondition"
              valuePropName="checked"
              className={styles.block}
              style={{ gridArea: 'enableCompareLoaderVersions' }}
            >
              <Checkbox
                onChange={() => {
                  setCheckedCompareLoad(!checkedCompareLoad);
                }}
              >
                Enable Compare Loader Versions
              </Checkbox>
            </Form.Item>
          </>
        )}
        {deviceSettings?.allowDowngradeUr && (
          <Form.Item
            name="forceUpdate"
            valuePropName="checked"
            className={styles.block}
            style={{ gridArea: 'forcedRule' }}
          >
            <Checkbox>Forced rule</Checkbox>
          </Form.Item>
        )}
        {deviceSettings?.urSendAllAtOnce && (
          <Form.Item
            name="sendAllFirmwares"
            valuePropName="checked"
            className={styles.block}
            style={{ gridArea: 'sendFirmOnce' }}
          >
            <Checkbox>Send all firmwares at once</Checkbox>
          </Form.Item>
        )}
      </Form>
    </Card>
  ) : null;
};

export default React.memo(EditRuleModal);
